import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, User, MapPin, Clock, Heart, Sparkles, List as ListIcon } from "lucide-react";
import StateBadge from "@/components/agenda/StateBadge";
import { useAgendaFilters } from "@/lib/useAgendaFilters";
import { Calendar as DatePickerCalendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { it as itLocale } from "date-fns/locale";
import {
  STATE_LABEL,
  STATE_SHORT,
  STATE_DOT_COLOR,
  STATE_CARD_CLASS,
  ALL_STATES,
} from "@/lib/agendaStates";

const MONTH_LABELS = [
  "Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno",
  "Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre",
];
const DAY_LABELS = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];
const DAY_LABELS_LONG = ["Lunedì","Martedì","Mercoledì","Giovedì","Venerdì","Sabato","Domenica"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function startOfWeek(d) {
  const r = new Date(d);
  const dow = (r.getDay() + 6) % 7; // Mon = 0
  r.setDate(r.getDate() - dow);
  r.setHours(0, 0, 0, 0);
  return r;
}
function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

// CSS class derivata da v6 visual coding
function stateChipClass(state) {
  switch (state) {
    case "confermato": return "bg-gradient-to-r from-[#FFF8EC] to-[#F4E4C3] border border-[#7B6A45] text-[#7B6A45] font-semibold";
    case "inviato":    return "bg-amber-50 border border-amber-500 text-amber-800";
    case "bozza":      return "bg-white border border-dashed border-neutral-400 text-neutral-600 italic";
    case "rifiutato":  return "bg-neutral-100 border border-neutral-300 text-neutral-400 line-through opacity-60";
    case "prova":      return "bg-violet-50 border border-violet-400 text-violet-800";
    case "personal":   return "bg-neutral-50 border border-neutral-300 text-neutral-700";
    default:           return "bg-neutral-100 border border-neutral-300 text-neutral-700";
  }
}

/**
 * Agenda v6 — vista calendario derivata.
 *
 * Single source of truth: il backend deriva lo stato da quote/contract status.
 * Le 3 viste (mese/settimana/giorno) consumano gli STESSI dati con la stessa
 * logica di filtraggio — solo il rendering differisce.
 */
export default function Agenda() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => new Date());
  const [view, setView] = useState("month"); // month | week | day
  const [team, setTeam] = useState([]);
  const [data, setData] = useState({ weddings: [], trials: [], personal_blocks: [] });
  const [loading, setLoading] = useState(true);

  const filters = useAgendaFilters();

  // Range dinamico in base alla view
  const range = useMemo(() => {
    if (view === "month") {
      const monthStart = startOfMonth(cursor);
      const dayOfWeek = (monthStart.getDay() + 6) % 7;
      const gridStart = addDays(monthStart, -dayOfWeek);
      const gridEnd = addDays(gridStart, 41);
      return { from: gridStart, to: gridEnd };
    }
    if (view === "week") {
      const ws = startOfWeek(cursor);
      return { from: ws, to: addDays(ws, 6) };
    }
    if (view === "list") {
      // Vista lista — finestra ampia centrata su `cursor`:
      // 6 mesi indietro + 18 mesi avanti. Sufficient per il pianning
      // operativo (recap matrimoni passati + tutto il book futuro) e
      // sotto la soglia di paginazione (tipicamente <500 eventi).
      const from = addDays(cursor, -180);
      const to = addDays(cursor, 540);
      from.setHours(0, 0, 0, 0);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    }
    // day
    const d = new Date(cursor);
    d.setHours(0, 0, 0, 0);
    return { from: d, to: d };
  }, [cursor, view]);

  useEffect(() => {
    let cancel = false;
    async function load() {
      setLoading(true);
      const params = { date_from: isoDate(range.from), date_to: isoDate(range.to) };
      try {
        // Use the shared `api` client (Bearer-token aware) instead of raw
        // axios — raw axios + `withCredentials:true` relied SOLELY on the
        // `access_token` cookie. In Safari ITP / Chrome partitioned-storage
        // contexts (embedded preview, third-party iframes, strict privacy
        // mode) that cookie is silently dropped, the endpoint 401s, the
        // catch below swallows the error → agenda renders empty even
        // though MongoDB has the events. `api.get` always sends the
        // Authorization header from the in-memory/localStorage token, so
        // the call succeeds in every browser context. Root cause of the
        // "agenda intermittente" bug.
        const [agendaRes, teamRes] = await Promise.all([
          api.get("/agenda/events", { params }),
          api.get("/team").catch(() => ({ data: [] })),
        ]);
        if (!cancel) {
          const payload = agendaRes.data || { weddings: [], trials: [], personal_blocks: [] };
          setData(payload);
          setTeam(teamRes.data || []);
          // Diagnostic logs — keep cheap so we can leave them on in prod.
          // If "agenda vuota" recurs we'll see the actual response shape
          // and the requested range in the console immediately.
          // eslint-disable-next-line no-console
          console.info("[agenda] fetch ok", {
            range: params,
            weddings: (payload.weddings || []).length,
            trials: (payload.trials || []).length,
            personal_blocks: (payload.personal_blocks || []).length,
            team: (teamRes.data || []).length,
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[agenda] fetch FAILED", {
          range: params,
          status: e?.response?.status,
          message: e?.message,
        });
        if (!cancel) {
          // Reset to empty SO the UI renders the "no events" placeholder
          // rather than the previous render's stale weddings — otherwise
          // the operator would see events that may belong to a stale range.
          setData({ weddings: [], trials: [], personal_blocks: [] });
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    load();
    return () => { cancel = true; };
  }, [range.from, range.to]);

  // Aggrega tutti gli eventi normalizzati (stessa logica per le 3 viste)
  const eventsByDate = useMemo(() => {
    const map = new Map();
    const allEvents = [];
    (data.weddings || []).forEach((w) => allEvents.push({
      ...w,
      assignedTeamIds: [w.makeup_artist?.id, w.hair_artist?.id, ...(w.assistants || []).map((a) => a?.id)].filter(Boolean),
      assignedToMe: false,
    }));
    (data.trials || []).forEach((t) => allEvents.push({
      ...t,
      assignedTeamIds: [t.artist?.id].filter(Boolean),
      assignedToMe: false,
    }));
    (data.personal_blocks || []).forEach((b) => allEvents.push({ ...b, state: "personal", assignedTeamIds: [], assignedToMe: true }));

    let kept = 0;
    let filteredOut = 0;
    for (const ev of allEvents) {
      if (!filters.isVisible(ev) && ev.state !== "personal") {
        filteredOut += 1;
        continue;
      }
      kept += 1;
      const key = ev.date;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    }
    // Diagnostic — if "agenda piena di eventi nel backend ma vuota in UI"
    // recurs, this tells us whether the filter is hiding them.
    // eslint-disable-next-line no-console
    console.info("[agenda] filter pass", {
      total: allEvents.length,
      kept,
      filtered_out: filteredOut,
      states_filter: Array.from(filters.states || []),
      teams_filter: Array.from(filters.teams || []),
      only_mine: filters.onlyMine,
      show_rejected: filters.showRejected,
    });
    // Ordina ogni giorno per orario (ev senza orario → in cima)
    for (const list of map.values()) {
      list.sort((a, b) => {
        const ta = a.ceremony_time || a.time || "";
        const tb = b.ceremony_time || b.time || "";
        return ta.localeCompare(tb);
      });
    }
    return map;
  }, [data, filters]);

  // Navigation: scope-aware (mese/settimana/giorno)
  const goPrev = () => {
    if (view === "month") setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
    else if (view === "week") setCursor((c) => addDays(c, -7));
    else setCursor((c) => addDays(c, -1));
  };
  const goNext = () => {
    if (view === "month") setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
    else if (view === "week") setCursor((c) => addDays(c, 7));
    else setCursor((c) => addDays(c, 1));
  };
  const goToday = () => setCursor(new Date());

  // Label dinamica
  const headerLabel = useMemo(() => {
    if (view === "month") return `${MONTH_LABELS[cursor.getMonth()]} ${cursor.getFullYear()}`;
    if (view === "week") {
      const ws = startOfWeek(cursor);
      const we = addDays(ws, 6);
      const sameMonth = ws.getMonth() === we.getMonth();
      if (sameMonth) {
        return `${ws.getDate()}–${we.getDate()} ${MONTH_LABELS[ws.getMonth()]} ${ws.getFullYear()}`;
      }
      return `${ws.getDate()} ${MONTH_LABELS[ws.getMonth()]} – ${we.getDate()} ${MONTH_LABELS[we.getMonth()]} ${we.getFullYear()}`;
    }
    if (view === "list") {
      return `Elenco eventi`;
    }
    return `${DAY_LABELS_LONG[(cursor.getDay() + 6) % 7]} ${cursor.getDate()} ${MONTH_LABELS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  }, [cursor, view]);

  const todayIso = isoDate(new Date());

  // Click handler comune a tutte le viste — naviga al cliente
  const handleEventClick = (ev) => {
    if (ev.client_id) navigate(`/app/clients/${ev.client_id}`);
  };

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto" data-testid="agenda-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <span className="text-[11px] tracking-[0.2em] uppercase text-[#7B6A45] font-medium">Agenda</span>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="group flex items-center gap-2 font-serif-heading text-2xl md:text-4xl tracking-tight hover:text-[#7B6A45] transition-colors"
                data-testid="agenda-header-label"
                title="Clicca per saltare a una data"
              >
                <span>{headerLabel}</span>
                <CalendarIcon className="w-4 h-4 md:w-5 md:h-5 text-neutral-400 group-hover:text-[#7B6A45] transition-colors" strokeWidth={1.5} />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" data-testid="agenda-date-picker">
              <DatePickerCalendar
                mode="single"
                selected={cursor}
                onSelect={(d) => { if (d) setCursor(d); }}
                locale={itLocale}
                weekStartsOn={1}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={goPrev} className="w-9 h-9 rounded-full border border-[#EAE3D8] hover:bg-white flex items-center justify-center" data-testid="agenda-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={goToday} className="px-3 h-9 rounded border border-[#EAE3D8] hover:bg-white text-sm" data-testid="agenda-today">Oggi</button>
          <button onClick={goNext} className="w-9 h-9 rounded-full border border-[#EAE3D8] hover:bg-white flex items-center justify-center" data-testid="agenda-next"><ChevronRight className="w-4 h-4" /></button>
          <div className="inline-flex rounded-lg bg-neutral-200 p-0.5 ml-2" data-testid="agenda-view-switch">
            {["month","week","day","list"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1.5 rounded-md text-[12.5px] font-medium inline-flex items-center gap-1.5 ${view === v ? "bg-white shadow-sm text-neutral-900" : "text-neutral-500"}`}
                data-testid={`agenda-view-${v}`}
              >
                {v === "list" && <ListIcon className="w-3.5 h-3.5" strokeWidth={1.7} />}
                {v === "month" ? "Mese" : v === "week" ? "Settimana" : v === "day" ? "Giorno" : "Lista"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filtri stato */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="agenda-filter-states">
        <button
          onClick={filters.setAllStates}
          className={`px-3 h-8 rounded-full text-[12px] font-medium border ${filters.statesAllActive ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-[#EAE3D8] text-neutral-600"}`}
          data-testid="filter-state-all"
        >
          Tutti
        </button>
        {ALL_STATES.filter((s) => s !== "rifiutato").map((s) => {
          const active = filters.states.has(s);
          return (
            <button
              key={s}
              onClick={() => filters.toggleState(s)}
              className={`px-2.5 h-8 rounded-full text-[12px] font-medium border inline-flex items-center gap-1.5 ${active ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-[#EAE3D8] text-neutral-700"}`}
              data-testid={`filter-state-${s}`}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: STATE_DOT_COLOR[s] }} />
              {STATE_SHORT[s]} · {STATE_LABEL[s]}
            </button>
          );
        })}
        <button
          onClick={() => filters.setShowRejected(!filters.showRejected)}
          className={`px-2.5 h-8 rounded-full text-[12px] font-medium border inline-flex items-center gap-1.5 ${filters.showRejected ? "bg-neutral-700 text-white border-neutral-700" : "bg-white border-[#EAE3D8] text-neutral-400 line-through"}`}
          data-testid="filter-state-rifiutato"
        >
          <span className="w-2 h-2 rounded-full" style={{ background: STATE_DOT_COLOR.rifiutato }} />
          RIF · Rifiutati
        </button>
      </div>

      {/* Filtri team */}
      {team.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5" data-testid="agenda-filter-team">
          <button
            onClick={() => filters.setOnlyMine(!filters.onlyMine)}
            className={`px-2.5 h-8 rounded-full text-[12px] font-semibold border inline-flex items-center gap-1.5 ${filters.onlyMine ? "bg-[#FFF8EC] border-[#C8A97E] text-[#7B6A45]" : "bg-white border-[#EAE3D8] text-neutral-600"}`}
            data-testid="filter-only-mine"
          >
            <User className="w-3 h-3" /> Solo miei
          </button>
          <span className="text-neutral-300">·</span>
          <button
            onClick={filters.setAllTeams}
            className={`px-2.5 h-8 rounded-full text-[12px] font-medium border ${filters.teamsAllActive ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-[#EAE3D8] text-neutral-600"}`}
            data-testid="filter-team-all"
          >
            Team Tutti
          </button>
          {team.map((t) => {
            const active = filters.teams.has(t.id);
            return (
              <button
                key={t.id}
                onClick={() => filters.toggleTeam(t.id)}
                className={`px-2.5 h-8 rounded-full text-[12px] font-medium border inline-flex items-center gap-1.5 ${active ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-[#EAE3D8] text-neutral-700"}`}
                data-testid={`filter-team-${t.id}`}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: t.color || "#999" }} />
                {t.name?.split(" ")[0] || "—"}
              </button>
            );
          })}
        </div>
      )}

      {/* VISTA MESE */}
      {view === "month" && (
        <AgendaMonth cursor={cursor} eventsByDate={eventsByDate} onEventClick={handleEventClick} todayIso={todayIso} />
      )}

      {/* VISTA SETTIMANA */}
      {view === "week" && (
        <AgendaWeek cursor={cursor} eventsByDate={eventsByDate} onEventClick={handleEventClick} todayIso={todayIso} />
      )}

      {/* VISTA GIORNO */}
      {view === "day" && (
        <AgendaDay cursor={cursor} eventsByDate={eventsByDate} onEventClick={handleEventClick} todayIso={todayIso} />
      )}

      {/* VISTA LISTA */}
      {view === "list" && (
        <AgendaList eventsByDate={eventsByDate} onEventClick={handleEventClick} todayIso={todayIso} />
      )}

      {/* Stats footer */}
      <div className="mt-4 flex flex-wrap items-center gap-3 text-[11.5px] text-neutral-600 px-1">
        <span>Mostrati nel periodo:</span>
        {ALL_STATES.filter((s) => s !== "rifiutato" || filters.showRejected).map((s) => {
          const cnt = Array.from(eventsByDate.values()).flat().filter(
            (ev) => ev.state === s && filters.isVisible(ev)
          ).length;
          return (
            <span key={s} className="inline-flex items-center gap-1.5" data-testid={`agenda-stat-${s}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: STATE_DOT_COLOR[s] }} />
              {cnt} {STATE_LABEL[s]}{cnt !== 1 ? "·" : ""}
            </span>
          );
        })}
        {loading && <span className="text-neutral-400">caricamento…</span>}
      </div>
    </div>
  );
}

/* ===================================================================
 *  AgendaMonth — vista mese 6×7
 * =================================================================== */
function AgendaMonth({ cursor, eventsByDate, onEventClick, todayIso }) {
  const monthStart = startOfMonth(cursor);
  const dow = (monthStart.getDay() + 6) % 7;
  const gridStart = addDays(monthStart, -dow);
  const gridStartKey = gridStart.toISOString();
  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 42; i++) arr.push(addDays(gridStart, i));
    return arr;
  }, [gridStartKey]);

  return (
    <div className="bg-white border border-[#EAE3D8] rounded-2xl overflow-hidden" data-testid="agenda-month-grid">
      <div className="grid grid-cols-7 bg-[#FFF8EC] border-b border-[#EAE3D8]">
        {DAY_LABELS.map((d) => (
          <div key={d} className="text-center py-2 text-[10.5px] uppercase tracking-wider text-[#7B6A45] font-semibold">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-[#EAE3D8]">
        {days.map((d) => {
          const k = isoDate(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = k === todayIso;
          const events = eventsByDate.get(k) || [];
          return (
            <div
              key={k}
              className={`bg-white min-h-[88px] md:min-h-[110px] p-1.5 ${inMonth ? "" : "opacity-40"} ${isToday ? "bg-[#FFF8EC]" : ""}`}
              data-testid={`agenda-cell-${k}`}
            >
              <div className={`text-[11px] mb-1 ${isToday ? "font-bold text-[#7B6A45]" : "text-neutral-600"}`}>
                {d.getDate()}
              </div>
              <div className="space-y-0.5">
                {events.slice(0, 3).map((ev) => (
                  <button
                    key={ev.id}
                    onClick={() => onEventClick(ev)}
                    className={`w-full text-left text-[10px] leading-tight px-1.5 py-1 rounded ${stateChipClass(ev.state)} truncate flex items-center gap-1`}
                    title={`${ev.client_name || ev.title || ""} · ${STATE_LABEL[ev.state] || ev.state}`}
                    data-testid={`agenda-event-${ev.id}`}
                  >
                    {(ev.kind === "wedding" || ev.kind === "trial") && (
                      <StateBadge
                        state={ev.kind === "trial" ? "prova" : ev.state}
                        withIcon={false}
                        className="!text-[8px] !px-1 !py-0"
                      />
                    )}
                    <span className="truncate">{ev.client_name || ev.title}</span>
                  </button>
                ))}
                {events.length > 3 && (
                  <div className="text-[9.5px] text-neutral-500 pl-1">+{events.length - 3} altri</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ===================================================================
 *  AgendaWeek — vista settimana
 *
 *  • Desktop (≥ md): grid 7 colonne classico (header verticale, eventi
 *    impilati). Coerente con calendari desktop standard.
 *  • Mobile (< md): row-per-day. 7 colonne su 375px = 53px ciascuna,
 *    illeggibili; il pattern row-per-day rispecchia le settimane di
 *    Apple Calendar in modalità "List" e mantiene il giorno come unità
 *    primaria, scorrevole verticalmente con un pollice. La logica dati,
 *    i filtri (stato + team + onlyMine), e il click → cliente sono
 *    condivisi tra le due viste.
 * =================================================================== */
function AgendaWeek({ cursor, eventsByDate, onEventClick, todayIso }) {
  const ws = startOfWeek(cursor);
  const wsKey = ws.toISOString();
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(ws, i)),
    [wsKey],
  );

  return (
    <>
      {/* ── Mobile (< md): row-per-day list ── */}
      <div className="md:hidden bg-white border border-[#EAE3D8] rounded-2xl overflow-hidden" data-testid="agenda-week-list">
        {days.map((d) => {
          const k = isoDate(d);
          const isToday = k === todayIso;
          const events = eventsByDate.get(k) || [];
          const dow = (d.getDay() + 6) % 7;
          return (
            <div
              key={k}
              className={`border-b border-[#EAE3D8] last:border-b-0 ${isToday ? "bg-[#FFF8EC]" : ""}`}
              data-testid={`agenda-week-row-${k}`}
            >
              {/* Day header — sticky-like, always visible even when empty
                  so the user can scan all 7 days at a glance. */}
              <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[#EAE3D8]/60">
                <div className="text-center w-12 shrink-0">
                  <div className={`text-[10px] uppercase tracking-wider ${isToday ? "text-[#7B6A45] font-bold" : "text-neutral-500"}`}>
                    {DAY_LABELS[dow]}
                  </div>
                  <div className={`font-serif-heading text-2xl leading-none mt-0.5 ${isToday ? "text-[#7B6A45]" : "text-neutral-800"}`}>
                    {d.getDate()}
                  </div>
                </div>
                <div className="flex-1 text-[11px] text-neutral-500">
                  {isToday ? "Oggi" : `${events.length} ${events.length === 1 ? "evento" : "eventi"}`}
                </div>
              </div>
              {/* Events for this day — vertical stack, each card full-width
                  with state pill, time, name, location. Touch-friendly
                  44px+ tap area. */}
              {events.length === 0 ? (
                <div className="px-4 py-2.5 text-[12px] text-neutral-300 italic">Nessun evento</div>
              ) : (
                <div className="divide-y divide-[#EAE3D8]/60">
                  {events.map((ev) => (
                    <button
                      key={ev.id}
                      onClick={() => onEventClick(ev)}
                      className="w-full text-left px-4 py-3 flex items-start gap-3 active:bg-[#FFF8EC]/60 transition-colors min-h-[56px]"
                      data-testid={`agenda-event-${ev.id}`}
                    >
                      <div className="shrink-0">
                        <StateBadge
                          state={ev.kind === "trial" ? "prova" : ev.state}
                          className="!text-[9px]"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-neutral-900 truncate flex items-center gap-2">
                          {ev.client_name || ev.title}
                          {ev.kind === "trial" && ev.trial_label && (
                            <span className="text-[10px] text-violet-700 font-normal">· Prova {ev.trial_label.toLowerCase()}</span>
                          )}
                        </div>
                        {ev.location && (
                          <div className="text-[11px] text-neutral-500 truncate flex items-center gap-1 mt-0.5">
                            <MapPin className="w-2.5 h-2.5 shrink-0" />
                            {ev.location}
                          </div>
                        )}
                      </div>
                      {(ev.ceremony_time || ev.time) && (
                        <div className="text-[12px] font-mono text-neutral-700 tabular-nums shrink-0 pt-0.5">
                          {ev.ceremony_time || ev.time}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Desktop (md+): grid 7 colonne classico ── */}
      <div className="hidden md:block bg-white border border-[#EAE3D8] rounded-2xl overflow-hidden" data-testid="agenda-week-grid">
        <div className="grid grid-cols-7 gap-px bg-[#EAE3D8] min-h-[500px]">
          {days.map((d) => {
            const k = isoDate(d);
            const isToday = k === todayIso;
            const events = eventsByDate.get(k) || [];
            const dow = (d.getDay() + 6) % 7;
            return (
              <div
                key={k}
                className={`bg-white p-2 ${isToday ? "bg-[#FFF8EC]" : ""}`}
                data-testid={`agenda-week-col-${k}`}
              >
                <div className={`text-center pb-2 mb-2 border-b border-[#EAE3D8] ${isToday ? "border-[#7B6A45]" : ""}`}>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500">{DAY_LABELS[dow]}</div>
                  <div className={`font-serif-heading text-2xl mt-1 ${isToday ? "text-[#7B6A45]" : "text-neutral-800"}`}>
                    {d.getDate()}
                  </div>
                </div>
                <div className="space-y-1.5">
                  {events.length === 0 ? (
                    <div className="text-[10px] text-neutral-300 text-center pt-4">—</div>
                  ) : (
                    events.map((ev) => (
                      <button
                        key={ev.id}
                        onClick={() => onEventClick(ev)}
                        className={`w-full text-left p-2 rounded ${stateChipClass(ev.state)} hover:brightness-95 transition-all`}
                        data-testid={`agenda-event-${ev.id}-d`}
                      >
                        <div className="flex items-center gap-1 mb-0.5">
                          <StateBadge
                            state={ev.kind === "trial" ? "prova" : ev.state}
                            className="!text-[8px]"
                          />
                          {(ev.ceremony_time || ev.time) && (
                            <span className="text-[10px] font-mono text-neutral-600 ml-auto">{ev.ceremony_time || ev.time}</span>
                          )}
                        </div>
                        <div className="text-[11px] font-medium leading-tight truncate">
                          {ev.client_name || ev.title}
                        </div>
                        {ev.kind === "trial" && ev.trial_label && (
                          <div className="text-[9.5px] text-violet-700 mt-0.5">Prova {ev.trial_label.toLowerCase()}</div>
                        )}
                        {ev.location && (
                          <div className="text-[9.5px] text-neutral-500 truncate mt-0.5 flex items-center gap-0.5">
                            <MapPin className="w-2.5 h-2.5 shrink-0" />{ev.location}
                          </div>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ===================================================================
 *  AgendaDay — 1 colonna, card grandi con tutto il dettaglio
 * =================================================================== */
function AgendaDay({ cursor, eventsByDate, onEventClick, todayIso }) {
  const k = isoDate(cursor);
  const isToday = k === todayIso;
  const events = eventsByDate.get(k) || [];

  if (events.length === 0) {
    return (
      <div className="bg-white border border-[#EAE3D8] rounded-2xl p-12 text-center" data-testid="agenda-day-empty">
        <CalendarIcon className="w-12 h-12 mx-auto text-[#C8A97E]/60 mb-3" strokeWidth={1.2} />
        <h3 className="font-serif-heading text-xl mb-2">Nessun evento in agenda</h3>
        <p className="text-sm text-neutral-500 max-w-md mx-auto">
          {isToday ? "Goditi la giornata libera." : "Nessun matrimonio, prova o blocco personale in questo giorno."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="agenda-day-list">
      {events.map((ev) => {
        const isWedding = ev.kind === "wedding";
        const isTrial = ev.kind === "trial";
        const Icon = isWedding ? Heart : isTrial ? Sparkles : CalendarIcon;
        const cardCls = STATE_CARD_CLASS[isTrial ? "prova" : ev.state] || "bg-white border border-neutral-200";
        return (
          <button
            key={ev.id}
            onClick={() => onEventClick(ev)}
            className={`w-full text-left rounded-2xl p-5 ${cardCls} hover:shadow-md transition-all group`}
            data-testid={`agenda-day-card-${ev.id}`}
          >
            <div className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-full bg-white border border-current/30 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5" strokeWidth={1.5} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <StateBadge state={isTrial ? "prova" : ev.state} />
                  {isWedding && <span className="text-[11px] uppercase tracking-wider text-[#7B6A45] font-semibold">Matrimonio</span>}
                  {isTrial && <span className="text-[11px] uppercase tracking-wider text-violet-700 font-semibold">Prova {ev.trial_label?.toLowerCase()}</span>}
                </div>
                <div className="font-serif-heading text-2xl md:text-3xl mb-2 truncate">{ev.client_name || ev.title}</div>
                <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-neutral-700">
                  {(ev.ceremony_time || ev.time) && (
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-4 h-4" strokeWidth={1.5} />
                      {ev.ceremony_time || ev.time}
                      {isWedding && ev.work_start_time && (
                        <span className="text-xs text-neutral-500 ml-1">· inizio lavoro {ev.work_start_time}</span>
                      )}
                    </span>
                  )}
                  {ev.location && (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="w-4 h-4" strokeWidth={1.5} />
                      <span className="truncate max-w-[200px]">{ev.location}</span>
                    </span>
                  )}
                  {ev.guests > 0 && (
                    <span className="inline-flex items-center gap-1.5">
                      <User className="w-4 h-4" strokeWidth={1.5} />
                      {ev.guests} persone
                    </span>
                  )}
                </div>
                {(isWedding && (ev.makeup_artist || ev.hair_artist || (ev.assistants && ev.assistants.length > 0))) && (
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-current/15">
                    {ev.makeup_artist && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/70 text-[11px] border border-current/20">
                        <span className="w-2 h-2 rounded-full" style={{ background: ev.makeup_artist.color || "#999" }} />
                        Trucco · {ev.makeup_artist.name}
                      </span>
                    )}
                    {ev.hair_artist && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/70 text-[11px] border border-current/20">
                        <span className="w-2 h-2 rounded-full" style={{ background: ev.hair_artist.color || "#999" }} />
                        Capelli · {ev.hair_artist.name}
                      </span>
                    )}
                    {(ev.assistants || []).map((a) => (
                      <span key={a.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/70 text-[11px] border border-current/20">
                        <span className="w-2 h-2 rounded-full" style={{ background: a.color || "#999" }} />
                        {a.name}
                      </span>
                    ))}
                  </div>
                )}
                {isTrial && ev.artist && (
                  <div className="mt-3 pt-3 border-t border-current/15">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/70 text-[11px] border border-current/20">
                      <span className="w-2 h-2 rounded-full" style={{ background: ev.artist.color || "#999" }} />
                      {ev.artist.name}
                    </span>
                  </div>
                )}
                {isWedding && typeof ev.quote_amount === "number" && ev.quote_amount > 0 && (
                  <div className="text-[11px] text-neutral-500 mt-2">
                    Preventivo: € {ev.quote_amount.toFixed(2)}
                    {ev.state === "inviato" && ev.quote_attesa_gg > 0 && (
                      <span className="ml-2 text-amber-700">· attesa risposta da {ev.quote_attesa_gg} gg</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}


/**
 * AgendaList — vista elenco cronologica.
 *
 * Mostra tutti gli eventi nel `eventsByDate` (già pre-filtrato a monte
 * con stato + team + only-mine + show-rejected) sotto forma di righe
 * piatte ordinate per data ↑, segnando in rosso gli eventi passati e
 * con un pallino chiaro quelli futuri.
 *
 * Click sulla riga → naviga alla scheda cliente (via `onEventClick`).
 * Ricerca per nome sposa: input local-state, filtraggio case-insensitive
 * lato client (non rifa la fetch — già abbiamo tutta la finestra di 24
 * mesi in memoria).
 */
function AgendaList({ eventsByDate, onEventClick, todayIso }) {
  const [query, setQuery] = React.useState("");
  // Flatten + sort: cronologico per data, poi per orario all'interno della
  // stessa data.
  const rows = React.useMemo(() => {
    const all = [];
    for (const [, list] of eventsByDate) all.push(...list);
    const q = query.trim().toLowerCase();
    const filtered = q
      ? all.filter((ev) => (ev.client_name || ev.title || "").toLowerCase().includes(q))
      : all;
    filtered.sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      const ta = a.ceremony_time || a.time || "";
      const tb = b.ceremony_time || b.time || "";
      return ta.localeCompare(tb);
    });
    return filtered;
  }, [eventsByDate, query]);

  const daysFrom = (iso) => {
    if (!iso) return 0;
    try {
      const [y, m, d] = iso.split("-").map(Number);
      const evt = new Date(y, m - 1, d).getTime();
      const today = new Date(); today.setHours(0, 0, 0, 0);
      return Math.round((evt - today.getTime()) / 86400000);
    } catch { return 0; }
  };

  return (
    <div className="mt-2" data-testid="agenda-list-view">
      {/* Search */}
      <div className="mb-4 flex items-center gap-2">
        <input
          type="search"
          placeholder="Cerca nome sposa…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 max-w-md h-10 px-3 rounded-md border border-[#EAE3D8] bg-white text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#C8A97E]/40"
          data-testid="agenda-list-search"
        />
        <span className="text-[12px] text-neutral-500" data-testid="agenda-list-count">
          {rows.length} {rows.length === 1 ? "evento" : "eventi"}
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-[#EAE3D8] rounded-md py-12 text-center text-sm text-neutral-500" data-testid="agenda-list-empty">
          {query ? "Nessun evento corrisponde alla ricerca." : "Nessun evento nel periodo selezionato."}
        </div>
      ) : (
        <ul className="divide-y divide-[#EAE3D8] border border-[#EAE3D8] rounded-md overflow-hidden bg-white">
          {rows.map((ev) => {
            const past = ev.date < todayIso;
            const dgg = daysFrom(ev.date);
            // Format date "ven 15 mag"
            let dateLabel = ev.date;
            try {
              const [y, m, d] = ev.date.split("-").map(Number);
              const dt = new Date(y, m - 1, d);
              dateLabel = `${DAY_LABELS[(dt.getDay() + 6) % 7].toLowerCase()} ${d} ${MONTH_LABELS[m - 1].slice(0, 3).toLowerCase()}`;
            } catch { /* fallback to ISO */ }
            const team = [
              ev.makeup_artist?.name,
              ev.hair_artist?.name,
              ...((ev.assistants || []).map((a) => a?.name).filter(Boolean)),
              ev.artist?.name,
            ].filter(Boolean).slice(0, 3).join(" · ");

            const isWeddingDay = ev.kind === "wedding";
            return (
              <li key={ev.id}>
                <button
                  type="button"
                  onClick={() => onEventClick && onEventClick(ev)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-neutral-50 transition-colors ${past ? "opacity-60" : ""}`}
                  data-testid={`agenda-list-row-${ev.id}`}
                  data-event-past={past ? "true" : "false"}
                >
                  {/* Data + giorni */}
                  <div className="shrink-0 w-28 sm:w-32">
                    <div className={`text-[13.5px] font-semibold ${past ? "text-red-700" : "text-neutral-900"}`}>{dateLabel}</div>
                    <div className="text-[11px] text-neutral-500">
                      {past ? `${Math.abs(dgg)}gg fa` : dgg === 0 ? "oggi" : `tra ${dgg}gg`}
                    </div>
                  </div>

                  {/* Stato chip */}
                  <div className="shrink-0">
                    <span className={`inline-flex items-center gap-1.5 px-2 h-6 rounded-full text-[11px] font-medium ${stateChipClass(ev.state)}`}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATE_DOT_COLOR[ev.state] }} />
                      {STATE_SHORT[ev.state] || ev.state}
                    </span>
                  </div>

                  {/* Nome + tipo */}
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-heading text-[15px] leading-tight truncate">{ev.client_name || ev.title || "—"}</div>
                    <div className="text-[11px] text-neutral-500 truncate">
                      {isWeddingDay ? "Matrimonio" : ev.trial_label ? `Prova ${ev.trial_label}` : "Blocco personale"}
                      {team && <span className="text-neutral-400"> · {team}</span>}
                    </div>
                  </div>

                  {/* Ora (solo per prove e blocchi) */}
                  {!isWeddingDay && (ev.time || ev.start_time) && (
                    <div className="shrink-0 text-[12px] tabular-nums text-neutral-500">
                      {ev.time || ev.start_time}
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
